import { Link, useNavigate } from 'react-router-dom';

import { COMPANY, CONTACT } from '@/constants/brand';
import { LOGO_J_PATH_32 } from '@/lib/logoMark';

type AnchorLink = { label: string; href: string };
type RouteLink = { label: string; to: string };
type FooterLink = AnchorLink | RouteLink;

const footerLinks: {
  produto: FooterLink[];
  suporte: RouteLink[];
  legal: RouteLink[];
} = {
  produto: [
    { label: 'Recursos', href: '/#recursos' },
    { label: 'Planos', href: '/#planos' },
    { label: 'Sobre', to: '/sobre' },
  ],
  suporte: [
    { label: 'FAQ', to: '/faq' },
    { label: 'Contato', to: '/contato' },
  ],
  legal: [
    { label: 'Termos de Uso', to: '/termos' },
    { label: 'Privacidade', to: '/privacidade' },
    { label: 'LGPD', to: '/lgpd' },
    { label: 'Cookies', to: '/cookies' },
  ],
};

function FooterLogo() {
  return (
    <div className="flex items-center gap-3">
      <svg viewBox="0 0 32 32" className="h-10 w-10 drop-shadow-[0_2px_8px_rgba(122,36,48,0.4)]" aria-hidden="true">
        <rect width="32" height="32" rx="5.33" fill="var(--color-primary)" />
        <path d={LOGO_J_PATH_32} fill="#ffffff" />
      </svg>
      <span className="text-base font-bold tracking-tight">
        <span className="text-primary">JURIS</span>APP
      </span>
    </div>
  );
}

const linkClass = 'text-sm text-muted-foreground no-underline transition-colors hover:text-primary';

/** Footer institucional compartilhado entre a landing e as páginas públicas. */
export function PublicFooter() {
  const navigate = useNavigate();

  // Âncoras como "/#recursos" funcionam de qualquer página: navega para a home
  // e desce até o ponto. Em rotas dedicadas, um href puro não teria esse efeito.
  const handleAnchor = (href: string) => {
    navigate('/');
    setTimeout(() => {
      document.querySelector(href.substring(1))?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const renderLink = (item: FooterLink) =>
    'href' in item ? (
      <a
        key={item.label}
        href={item.href}
        onClick={(e) => {
          e.preventDefault();
          handleAnchor(item.href);
        }}
        className={`${linkClass} cursor-pointer`}
      >
        {item.label}
      </a>
    ) : (
      <Link key={item.label} to={item.to} className={linkClass}>
        {item.label}
      </Link>
    );

  return (
    <footer className="border-t border-border bg-background py-12">
      <div className="container mx-auto max-w-screen-lg px-4">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-5">
          <div className="sm:col-span-2">
            <FooterLogo />
            <p className="mt-4 max-w-[300px] text-sm text-muted-foreground">
              Plataforma completa de gestão jurídica para advogados e escritórios que buscam eficiência e inovação.
            </p>
          </div>
          <div>
            <p className="mb-4 text-sm font-semibold text-foreground">Produto</p>
            <div className="flex flex-col gap-2">{footerLinks.produto.map(renderLink)}</div>
          </div>
          <div>
            <p className="mb-4 text-sm font-semibold text-foreground">Suporte</p>
            <div className="flex flex-col gap-2">{footerLinks.suporte.map(renderLink)}</div>
          </div>
          <div>
            <p className="mb-4 text-sm font-semibold text-foreground">Legal</p>
            <div className="flex flex-col gap-2">{footerLinks.legal.map(renderLink)}</div>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-border pt-8 sm:flex-row sm:items-start sm:justify-between">
          {/* Identificação da empresa: quem está por trás do site, com os dados do
              cartão CNPJ. O consumidor procura por isso, e parceiros a cruzam com o
              registro oficial — a verificação de empresa da Meta recusa site sem ela. */}
          <div className="flex flex-col gap-1 text-xs text-muted-foreground">
            <span>&copy; {new Date().getFullYear()} JurisApp. Todos os direitos reservados.</span>
            <span>
              {COMPANY.razaoSocial} · CNPJ {COMPANY.cnpj}
            </span>
            <span>
              {COMPANY.endereco} · {COMPANY.bairro}, {COMPANY.cidadeUf} · CEP {COMPANY.cep}
            </span>
            <a href={`mailto:${CONTACT.email}`} className="w-fit no-underline transition-colors hover:text-primary">
              {CONTACT.email}
            </a>
          </div>
          <span className="text-xs text-muted-foreground">Feito com dedicação para a advocacia brasileira</span>
        </div>
      </div>
    </footer>
  );
}

export default PublicFooter;
